import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import CustomButton from '../individual-components/button';
import PropTypes from 'prop-types';
import TeamDataJson from '@/assets/data/connected-teams.json';
import Slider from 'react-slick';
import LineStroke from '../individual-components/line-stroke';
import NextImage from '../NextImage';
import { createPortal } from 'react-dom';

// Main Team Component
const TeamData = ({ currentSlide, items }) => {
    let sliderRef = useRef(null);
    const [selectedMember, setSelectedMember] = useState(null);

    // Optimized data lookup
    const teamData = useMemo(() => {
        return items?.map(item => ({
            ...TeamDataJson[item?.key]
        }));
    }, [items]);

    useEffect(() => {
        if (sliderRef.current && sliderRef.current.slickGoTo) {
            sliderRef.current.slickGoTo(currentSlide);
        }
    }, [currentSlide]);

    const closeModal = () => setSelectedMember(null);
    const selectMember = useCallback((member) => () => setSelectedMember(member), []);

    return (
        <div style={{ minHeight: '700px' }} className="py-[60px] bg-[#F8F9FA] slider-container">
            <div className="max-w-7xl mx-auto">
                <Slider
                    infinite={false}
                    slidesToShow={1}
                    arrows={false}
                    swipeToSlide={false}
                    swipe={false}
                    ref={slider => (sliderRef.current = slider)}
                    lazyLoad='ondemand'
                >
                    {teamData?.map((team, index) => (
                        <div key={index}>
                            <div className="w-full flex md:flex-col items-center">
                                {/* Text Content */}
                                <div className="w-full text-center mb-0 max-w-[280px] sm:px-4 md:max-w-full mr-16 md:mr-0 md:mb-6">
                                    <h1 className="mb-4 text-4xl sm:text-2xl leading-8 text-gray-900 font-bold">
                                        {team?.title}
                                    </h1>
                                    <div className="sm:w-1/2 mx-auto">
                                        <LineStroke />
                                    </div>
                                    <p className="text-xl sm:text-lg xs:text-base leading-snug text-secondary mt-6">
                                        {team?.desc}
                                    </p>
                                    <div className="flex justify-center mt-4">
                                        <CustomButton
                                            href={'/hiretalent'}
                                            label='Get Started'
                                            variant='fill'
                                            tabIndex="-1"
                                        />
                                    </div>
                                </div>

                                {/* Team Members */}
                                <div className="w-full flex flex-wrap items-stretch justify-center gap-2 sm:px-6">
                                    <div className="w-full flex items-center justify-center">
                                        <MemberCard
                                            member={team?.lead}
                                            onClick={selectMember(team?.lead)}
                                            lead
                                        />
                                    </div>
                                    {team?.members?.map((member, idx) => (
                                        <MemberCard
                                            key={idx}
                                            member={member}
                                            index={idx}
                                            onClick={selectMember(member)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </Slider>
            </div>

            {/* Modal */}
            {selectedMember && <MemoizedMemberModal member={selectedMember} onClose={closeModal} />}
        </div>
    );
};

// Member Card Component
export const MemberCard = ({ member, index, lead, onClick }) => (
    <div
        onClick={onClick}
        key={member?.name + index}
        className='bg-[#A3AEBA99] cursor-pointer min-w-[250px] min-h-[180px] hover:shadow-2xl group hover:border-[#ee9038] select-none duration-300 border-4 rounded-3xl border-[#E8F2F6] text-center text-white xs:w-full w-5/12 xl:w-full flex-initial xl:max-w-[285px] p-4 flex flex-col justify-center employee-card'
    >
        <div className="flex items-center mb-3 justify-evenly">
            <div className="flex-none w-16 h-16">
                <NextImage
                    className="object-cover w-16 h-16 rounded-full overflow-hidden duration-300 border-2 border-transparent group-hover:border-[#ee9038]"
                    src={member?.picture}
                    alt={member?.name}
                />
            </div>
            <div>
                <div className="sm:text-sm font-semibold text-lg">
                    {member?.name}
                </div>
                <div className="sm:text-xs text-sm">{member?.location}</div>
                <div className="text-xl sm:text-sm font-bold">
                    {member?.position}
                </div>
            </div>
        </div>
        {!lead && member?.skills?.length ? (
            <div>
                <div className='flex w-full gap-4 mb-2 text-secondary'>
                    {member?.skills?.slice(0, 2).map(skill => (
                        <div key={skill} className={`w-1/2 flex items-center text-secondary justify-center px-2 py-1 bg-white rounded-full border-2 duration-300 group-hover:border-[#ee9038] border-[#E8F2F6]`} >
                            <p className="font-bold text-xs sm:text-[10px]">{skill}</p>
                        </div>
                    ))}
                </div>
                <div className={`max-w-[200px] mx-auto flex items-center text-secondary justify-center px-2 py-1 bg-white rounded-full border-2 duration-300 group-hover:border-[#ee9038] border-[#E8F2F6]`} >
                    <p className="font-bold text-xs sm:text-[10px]">{member?.skills[member?.skills?.length - 1] || ''}</p>
                </div>
            </div>
        ) : null}
    </div>
);

// Member Modal Component
const MemberModal = ({ member, onClose }) => {
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'auto'; };
    }, []);

    return createPortal(
        <div className="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50">
            <div className="bg-[#a9b4c0] relative border-4 rounded-[50px] border-[#E8F2F6] text-white w-full md:max-w-[700px] max-w-[900px] sm:p-5 p-10  shadow-2xl mx-8 flex flex-col justify-center md:justify-start max-h-[90vh] overflow-y-auto">
                <div onClick={onClose} className='xs:right-4 xs:top-4 absolute top-8 right-8 rotate-45 border-2 border-black rounded-full p-[5px] cursor-pointer opacity-70'>
                    <img src='/images/close.svg' alt='close modal' />
                </div>
                <div className="flex items-center justify-evenly sm:flex-col md:justify-center md:gap-10 sm:gap-2 mb-4 last:mb-0">
                    <img
                        className="md:w-32 md:h-32 sm:w-20 sm:h-20 object-cover w-56 h-56 md:mr-10 sm:mr-0 rounded-full"
                        alt="picture"
                        src={member?.picture}
                    />
                    <div className="flex flex-col md:gap-2 text-center gap-4 ">
                        <div className="sm:text-base text-3xl">{member?.name}</div>
                        <div className="sm:text-sm text-2xl">{member?.location}</div>
                        <div className="sm:text-lg text-4xl font-bold">{member?.position}</div>
                    </div>
                </div>

                <div className="md:mb-4 mb-8 max-w-[90%] max-h-[400px] overflow-y-auto mx-auto sm:text-sm break-words text-base">
                    {member?.desc || ''}
                </div>

                {/* Skills */}
                <div className="flex flex-wrap w-full text-secondary sm:gap-2 gap-4 justify-center">
                    {member?.skills?.map((skill, index) => (
                        <div
                            key={index}
                            className="flex items-center justify-center text-center md:p-1 p-2 bg-white rounded-full border-2 border-[#E8F2F6] w-full max-w-[200px]"
                        >
                            <p className="sm:text-xs text-lg">{skill}</p>
                        </div>
                    ))}
                </div>

                <div className='flex items-center justify-center mt-6'>
                    <CustomButton
                        label={`Hire ${member?.name}`}
                        href='/hiretalent'
                        shouldRenderArrow
                        variant='fill'
                    />
                </div>
            </div>
        </div>,
        document.body
    );
};

// Memoized Modal
const MemoizedMemberModal = React.memo(MemberModal);

// PropTypes
TeamData.propTypes = {
    currentSlide: PropTypes.number.isRequired,
    items: PropTypes.array.isRequired
};

MemberCard.propTypes = {
    member: PropTypes.object.isRequired,
    index: PropTypes.number,
    onClick: PropTypes.func.isRequired
};

MemberModal.propTypes = {
    member: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
};

export default TeamData;
